import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import StorageIcon from '@mui/icons-material/Storage';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SchoolIcon from '@mui/icons-material/School';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import DevicesIcon from '@mui/icons-material/Devices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import CodeIcon from '@mui/icons-material/Code';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const DesktopDevelopment: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Development technologies
  const technologies = [
    {
      title: ".NET / C#",
      description: "Modern, secure desktop applications with Microsoft's powerful development platform",
      icon: <CodeIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Java",
      description: "Cross-platform desktop solutions leveraging Java's 'write once, run anywhere' capabilities",
      icon: <IntegrationInstructionsIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Electron",
      description: "Web technologies-based desktop applications with access to native OS features",
      icon: <DevicesIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Qt / C++",
      description: "High-performance native applications with rich UI capabilities",
      icon: <SettingsIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
    {
      title: "Python",
      description: "Rapid development of desktop tools and utilities with excellent data processing capabilities",
      icon: <AutoFixHighIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
    },
  ];

  // Desktop application types
  const appTypes = [
    {
      title: "Enterprise Business Applications",
      description: "Streamline workflows and boost productivity with tailored business software",
      icon: <BusinessCenterIcon fontSize="large" />,
    },
    {
      title: "Industry-Specific Solutions",
      description: "Custom desktop applications designed for specific industry requirements",
      icon: <MiscellaneousServicesIcon fontSize="large" />,
    },
    {
      title: "Database Management Systems",
      description: "Powerful tools for data storage, retrieval, and manipulation",
      icon: <StorageIcon fontSize="large" />,
    },
    {
      title: "Point of Sale Systems",
      description: "Integrated retail management solutions with comprehensive business features",
      icon: <LocalMallIcon fontSize="large" />,
    },
    {
      title: "Financial & Accounting Software",
      description: "Secure applications for financial reporting, analysis, and compliance",
      icon: <AccountBalanceIcon fontSize="large" />,
    },
    {
      title: "Healthcare Management Systems",
      description: "HIPAA-compliant solutions for patient records and healthcare operations",
      icon: <LocalHospitalIcon fontSize="large" />,
    },
  ];

  // Development process
  const developmentProcess = [
    {
      step: "Requirements Analysis",
      description: "Detailed assessment of business needs, user workflows, and technical requirements"
    },
    {
      step: "Architecture Design",
      description: "Crafting robust application architecture for performance, scalability, and security"
    },
    {
      step: "UI/UX Design",
      description: "Creating intuitive interfaces that enhance productivity and user satisfaction"
    },
    {
      step: "Development",
      description: "Building functionality using modern coding practices and appropriate technologies"
    },
    {
      step: "Testing & QA",
      description: "Rigorous quality assurance to ensure reliability, performance, and security"
    },
    {
      step: "Deployment & Training",
      description: "Smooth implementation with comprehensive user training and documentation"
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Desktop Application Development
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Powerful software solutions to transform your business operations
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Custom Desktop Solutions for Business Success
              </Typography>
              <Typography paragraph>
                Right Track Technologies Limited specializes in developing powerful desktop software that transforms
                how businesses operate, enhances productivity, and delivers exceptional user experiences. Our expert
                development team creates custom desktop applications tailored to your specific business needs.
              </Typography>
              <Typography paragraph>
                Our expert developers create custom desktop applications that address your unique business 
                challenges, streamline workflows, and integrate seamlessly with your existing infrastructure. 
                We utilize modern technologies and development practices to deliver high-performance solutions 
                that drive efficiency and productivity.
              </Typography>
              <Typography paragraph>
                Whether you need to modernize legacy systems, automate complex processes, or create new 
                software to gain competitive advantage, our desktop development services provide the 
                robust foundation your business needs to succeed.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1581472723648-909f4851d4ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Desktop Application Development"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Technologies Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Desktop Development Technologies
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We match the right technology to your specific business requirements
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}>
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {tech.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {tech.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {tech.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* App Types Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Desktop Solutions for Every Industry
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Tailored desktop applications to address specific business challenges
          </Typography>
        </motion.div>
      
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 4,
          justifyContent: 'center'
        }}>
          {appTypes.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  {type.icon}
                </Box>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  {type.title}
                </Typography>
                <Typography color="text.secondary">
                  {type.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Development Process Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Desktop Development Process
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              A methodical approach to delivering robust desktop applications
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}>
            {developmentProcess.map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(33.333% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    position: 'relative',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Typography 
                    variant="h2" 
                    component="div" 
                    sx={{ 
                      position: 'absolute', 
                      top: -15, 
                      right: 10, 
                      opacity: 0.1,
                      fontWeight: 'bold',
                      color: theme.palette.primary.main,
                      fontSize: '5rem',
                      zIndex: 0
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                      {process.step}
                    </Typography>
                    <Typography color="text.secondary">
                      {process.description}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
          >
            Benefits of Custom Desktop Applications
          </Typography>
          <Typography 
            textAlign="center" 
            sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              mb: 6 
            }}
          >
            Why businesses choose custom desktop solutions
          </Typography>
        </motion.div>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                {
                  benefit: "Enhanced Performance",
                  description: "Faster processing speeds and optimized resource utilization",
                  icon: <SpeedIcon />
                },
                {
                  benefit: "Advanced Data Security",
                  description: "Greater control over sensitive information and compliance",
                  icon: <SecurityIcon />
                },
                {
                  benefit: "Offline Capabilities",
                  description: "Full functionality without requiring constant internet connectivity",
                  icon: <DesktopWindowsIcon />
                },
                {
                  benefit: "Business Process Automation",
                  description: "Streamlined workflows and reduced manual intervention",
                  icon: <SettingsIcon />
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.benefit} 
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <List>
              {[
                {
                  benefit: "Deep System Integration",
                  description: "Seamless connectivity with existing software and hardware",
                  icon: <IntegrationInstructionsIcon />
                },
                {
                  benefit: "Customized Functionality",
                  description: "Tailored features that address specific business requirements",
                  icon: <BuildIcon />
                },
                {
                  benefit: "Data Analytics & Reporting",
                  description: "Advanced business intelligence capabilities for better decisions",
                  icon: <QueryStatsIcon />
                },
                {
                  benefit: "Lower Total Cost of Ownership",
                  description: "Reduced long-term costs compared to recurring SaaS subscriptions",
                  icon: <SchoolIcon />
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.benefit} 
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 'bold' }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </Box>
      </Container>

      {/* Key Features Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Key Features of Our Desktop Solutions
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              What sets our desktop applications apart
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
          }}>
            {[
              {
                title: "Modern User Interface",
                description: "Intuitive, responsive designs that enhance productivity and user satisfaction",
                icon: <DevicesIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
              {
                title: "Enterprise-Grade Architecture",
                description: "Scalable, maintainable code structure designed for long-term business value",
                icon: <StorageIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
              {
                title: "Comprehensive Documentation",
                description: "Detailed user guides, technical documentation, and knowledge transfer",
                icon: <SchoolIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
              {
                title: "Dedicated Support",
                description: "Ongoing maintenance, updates, and technical assistance",
                icon: <SupportAgentIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: isMobile ? '100%' : 'calc(50% - 16px)' }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ bgcolor: '#0c2340', color: 'white', py: 10 }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center' }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Transform Your Business with Custom Desktop Software
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9 }}>
              Contact us today to discuss your desktop application development needs and discover
              how our custom solutions can streamline your operations and drive business growth.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              color="primary"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#0c2340',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Start Your Project
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default DesktopDevelopment; 